// Past projects - the one list behind both the /gallery page and the
// homepage's Featured Projects.
//
// Stored in the `projects` site setting and edited in Admin -> Storefront ->
// Content -> Projects. Every project appears on /gallery; the ones marked
// `featured` also appear on the homepage (its first three photos and first
// video). Before 2026-10 the homepage read its own `featured_projects`
// setting; that list is still read, as all-featured, when `projects` has not
// been saved yet, so nothing disappears in between.
//
// A saved project:
//   {
//     id:       'backyard-makeover-b06'  // stable anchor: /gallery#id
//     title, location, caption,
//     product:  'b06-premium-open-arm-awning'  // slug, optional - drives the
//                                              // gallery filter and the link
//     colour, size,                       // optional, shown as chips
//     featured: true | false,             // homepage
//     images:   [{ url, focus, label }],  // label optional, e.g. 'Before'
//     videos:   [{ mp4, webm, poster }],  // short silent loops
//   }
// Older saved shapes are accepted: a photo may be a bare URL string, and the
// single video_mp4 / video_webm / video_poster fields become videos[0].
//
// Australian English. Hyphens, never em dashes.

// The homepage collage shows up to three photos and needs at least two.
export const MAX_PROJECT_PHOTOS = 3;
export const MIN_PROJECT_PHOTOS = 2;

// `focus` is the vertical focal point, as a percentage, used where a tall
// phone photo is cropped into a short tile.
export const DEFAULT_FOCUS = 30;

export const BLANK_PROJECT = {
  id: '',
  title: '',
  location: '',
  caption: '',
  product: '',
  colour: '',
  size: '',
  featured: false,
  images: [],
  videos: [],
};

export const slugify = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 60);

function normalisePhoto(entry) {
  if (typeof entry === 'string') return entry ? { url: entry, focus: DEFAULT_FOCUS, label: '' } : null;
  if (!entry || typeof entry !== 'object' || !entry.url) return null;
  const f = Number(entry.focus);
  return { url: entry.url, focus: Number.isFinite(f) ? f : DEFAULT_FOCUS, label: entry.label || '' };
}

function normaliseVideos(p) {
  const list = Array.isArray(p.videos) ? p.videos : [];
  const videos = list
    .filter((v) => v && v.mp4)
    .map((v) => ({ mp4: v.mp4, webm: v.webm || '', poster: v.poster || '' }));
  if (!videos.length && p.video_mp4) {
    videos.push({ mp4: p.video_mp4, webm: p.video_webm || '', poster: p.video_poster || '' });
  }
  return videos;
}

// Any saved shape -> { id, title, location, caption, product, colour, size,
// featured, photos, videos }. Ids are made unique, so two projects with the
// same title still get their own anchors.
export function normaliseProjects(list, { legacyFeatured = false } = {}) {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .filter((p) => p && typeof p === 'object')
    .map((p, i) => {
      let id = slugify(p.id || p.title) || `project-${i + 1}`;
      while (seen.has(id)) id = `${id}-${i + 1}`;
      seen.add(id);
      return {
        id,
        title: p.title || '',
        location: p.location || '',
        caption: p.caption || '',
        product: p.product || '',
        colour: p.colour || '',
        size: p.size || '',
        featured: legacyFeatured ? true : !!p.featured,
        photos: (Array.isArray(p.images) ? p.images : []).map(normalisePhoto).filter(Boolean),
        videos: normaliseVideos(p),
      };
    });
}

// The project list from the site settings, whichever key holds it.
export function projectsFromSettings(settings = {}) {
  if (Array.isArray(settings.projects)) return normaliseProjects(settings.projects);
  return normaliseProjects(settings.featured_projects, { legacyFeatured: true });
}

// What the gallery shows: projects with at least one photo or video.
export const galleryProjects = (settings) =>
  projectsFromSettings(settings).filter((p) => p.photos.length || p.videos.length);

// What the homepage shows: featured projects with enough photos for the collage.
export const featuredProjects = (settings) =>
  projectsFromSettings(settings).filter((p) => p.featured && p.photos.length >= MIN_PROJECT_PHOTOS);

// The gallery link for a product: filtered to its projects when it has any,
// otherwise the whole gallery.
export function galleryHref(settings, productSlug) {
  const has = productSlug && galleryProjects(settings).some((p) => p.product === productSlug);
  return has ? `/gallery?product=${encodeURIComponent(productSlug)}` : '/gallery';
}

// Seed for the admin editor before anything has been saved.
export const DEFAULT_PROJECTS = [
  {
    ...BLANK_PROJECT,
    title: 'Dandenong Residence',
    location: 'Dandenong, VIC',
    caption: 'Louvered Pergola - 8m x 4.2m span, integrated LED lighting, automated rain sensor',
    featured: true,
  },
  {
    ...BLANK_PROJECT,
    title: 'AW01 Full Cassette Awning Install',
    caption: 'AW01 Full Cassette Awning - fully enclosed cassette, motorised, Spanish Sauleda fabric',
    product: 'aw01-full-box-awning',
    featured: true,
  },
];
